import './styles/ArticleMessagesSettings.scss';

import { Apps, Create, Domain, Mail } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { TreeViewBaseItem } from '@mui/x-tree-view';

import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useApplicationsQuery, useTemplatesQuery } from 'api/hooks/notifications';
import { SettingsTreeView } from 'app/components/SettingsTreeView';
import { translations } from 'locales/i18n';
import { ExtendedTreeItemProps } from 'types/AppTypes';
import usePageHeading from 'utils/hooks/usePageHeading';
import useQueryStringState from 'utils/hooks/useQueryStringState';

import { CreateMailTemplate } from './components/CreateMailTemplate';
import { LockedDepartmentsContent } from './components/LockedDepartmentsContent';
import { MailApplications } from './components/MailApplications';
import { MailTemplate } from './components/MailTemplate';

export enum TreeItemLabels {
  LOCKED = 'locked',
  APPLICATIONS = 'application',
  CREATETEMPLATE = 'createTemplate',
  MAILTEMPLATES = 'mailTemplates'
}

export type TreeItemLabelsValue = keyof typeof TreeItemLabels;

export default function ArticleMessagesSettings(): ReactElement {
  const { t } = useTranslation();

  const { data: applications } = useApplicationsQuery();

  const { data: templates } = useTemplatesQuery();

  const mailChildren = (): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    const mailChildren: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];
    applications.forEach(app => {
      //Leihbar is only shown in lender profile
      if (templates && app.name !== 'leihbar') {
        const newMailTemplateItem: TreeViewBaseItem<ExtendedTreeItemProps> = {
          id: `mail-${app.name}`,
          label: translations.pages.articleMessagesSettings.treeViewLabels[
            app.name.replace('-', '_')
          ]
            ? t(
                translations.pages.articleMessagesSettings.treeViewLabels[
                  app.name.replace('-', '_')
                ]()
              )
            : app.name,
          icon: <Mail />,
          templates: app.id
        };
        mailChildren.push(newMailTemplateItem);
      }
    });
    return mailChildren;
  };

  const ITEMS: TreeViewBaseItem<ExtendedTreeItemProps>[] = [
    {
      id: TreeItemLabels.LOCKED,
      label: t(translations.pages.articleMessagesSettings.treeViewLabels.locked()),
      icon: <Domain />
    },
    {
      id: TreeItemLabels.APPLICATIONS,
      label: t(translations.pages.articleMessagesSettings.treeViewLabels.applications()),
      icon: <Apps />
    },
    {
      id: TreeItemLabels.CREATETEMPLATE,
      label: t(translations.pages.articleMessagesSettings.treeViewLabels.createTemplate()),
      icon: <Create />
    },
    {
      id: TreeItemLabels.MAILTEMPLATES,
      label: t(translations.pages.articleMessagesSettings.treeViewLabels.mailTemplates()),
      icon: <Mail />,
      children: mailChildren()
    }
  ];

  const [area, setArea] = useQueryStringState('area', ITEMS[0].id);

  const renderSettingsTab = () => {
    switch (area) {
      case TreeItemLabels.LOCKED.toString():
        return <LockedDepartmentsContent />;

      case TreeItemLabels.APPLICATIONS.toString():
        return <MailApplications />;

      case TreeItemLabels.CREATETEMPLATE.toString():
        return <CreateMailTemplate />;

      default:
        if (area.startsWith('mail-')) {
          return renderMailTemplate(area);
        }
    }
  };

  const renderMailTemplate = area => {
    //Find mailTreeChildren
    const mails = ITEMS.find(item => item.id === TreeItemLabels.MAILTEMPLATES);

    if (!mails?.children) {
      return;
    }
    const templateGroup = mails.children.find(mail => mail.id === area);

    if (!templateGroup?.templates || templateGroup.templates.length === 0) {
      return;
    }
    return <MailTemplate appId={templateGroup.templates} key={templateGroup.templates[0]} />;
  };

  let expandedItems = '';

  usePageHeading({
    heading: t(translations.pages.articleMessagesSettings.title()),
    desktopOnly: true,
    className: 'article-messages-settings'
  });

  if (area.startsWith('mail-')) {
    expandedItems = 'mailTemplates';
  }
  return (
    <Grid container gap={2} justifyContent={'center'}>
      <SettingsTreeView
        items={ITEMS}
        activeView={area}
        setactiveView={setArea}
        expandedItems={[expandedItems]}
      ></SettingsTreeView>
      <Grid item xs>
        {renderSettingsTab()}
      </Grid>
    </Grid>
  );
}
